/**
 * Customer Portal Authentication Context.
 * OTP-based: requestOTP → verifyOTP → authenticated.
 * Mirrors AuthContext pattern but uses separate localStorage keys
 * so staff and customer sessions never collide.
 */

import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  customerPortalApi,
  CustomerProfile,
  OTPRequestResponse,
} from '../services/customerPortalApi';
import { safeStorage } from '../utils/safeStorage';

interface CustomerAuthContextType {
  customer: CustomerProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  requestOTP: (identifier: string, ssnOrAccount: string) => Promise<OTPRequestResponse>;
  verifyOTP: (identifier: string, code: string) => Promise<void>;
  logout: () => void;
}

const CustomerAuthContext = createContext<CustomerAuthContextType | undefined>(undefined);

export const CustomerAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [customer, setCustomer] = useState<CustomerProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    initializeAuth();

    const handleTokenCleared = () => {
      setCustomer(null);
    };

    window.addEventListener('customer-auth:token-cleared', handleTokenCleared);
    return () => {
      window.removeEventListener('customer-auth:token-cleared', handleTokenCleared);
    };
  }, []);

  const initializeAuth = async () => {
    try {
      const token = safeStorage.getItem('customer_access_token');
      if (token) {
        const profile = await customerPortalApi.getMe();
        setCustomer(profile);
      }
    } catch {
      customerPortalApi.logout();
      setCustomer(null);
    } finally {
      setIsLoading(false);
    }
  };

  const requestOTP = async (
    identifier: string,
    ssnOrAccount: string
  ): Promise<OTPRequestResponse> => {
    try {
      const result = await customerPortalApi.requestOTP(identifier, ssnOrAccount);
      toast.success(result.message);
      return result;
    } catch (error: unknown) {
      const message = extractErrorMessage(error) || 'Unable to send verification code';
      toast.error(message);
      throw error;
    }
  };

  const verifyOTP = async (identifier: string, code: string): Promise<void> => {
    try {
      setIsLoading(true);
      await customerPortalApi.verifyOTP(identifier, code);
      const profile = await customerPortalApi.getMe();
      setCustomer(profile);
      toast.success('Welcome back!');
    } catch (error: unknown) {
      customerPortalApi.logout();
      setCustomer(null);
      const message = extractErrorMessage(error) || 'Verification failed';
      toast.error(message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    customerPortalApi.logout();
    setCustomer(null);
    toast.success('Logged out');
  };

  return (
    <CustomerAuthContext.Provider
      value={{
        customer,
        isAuthenticated: !!customer,
        isLoading,
        requestOTP,
        verifyOTP,
        logout,
      }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = (): CustomerAuthContextType => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};

function extractErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { detail?: string } } }).response;
    if (response?.data?.detail) return response.data.detail;
  }
  return 'An error occurred';
}
